import { Button, DatePicker, Input, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  GET_DELIVERY_DRIVER_ACCOUNTS_FOR_SELECT,
  GetDeliveryDriverAccountsForSelectResponse,
} from '../../location-correction/graphql/queries/getDeliveryDriverAccountsForSelect';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getLastWeekDateTimeRange } from '../../../../utils/dateTimeUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';
import { useTranslation } from 'react-i18next';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

const { RangePicker } = DatePicker;

export type PendingFilterOptions = {
  residentialAddress: string;
  creatorId: number | undefined;
  createdAtRange?: [Dayjs | null, Dayjs | null] | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'all';
};

export const DEFAULT_PENDING_FILTER_OPTIONS: PendingFilterOptions = {
  residentialAddress: '',
  creatorId: undefined,
  createdAtRange: undefined,
  status: 'all',
};

interface PendingFilterAreaProps {
  filters: PendingFilterOptions;
  handleFilter: (options: PendingFilterOptions) => void;
}

const PendingFilterArea: React.FC<PendingFilterAreaProps> = ({ filters, handleFilter }) => {
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState<PendingFilterOptions>({ ...filters });
  const [driverSearchText, setDriverSearchText] = useState<string>('');
  const hasUserEditedCreatedAtRangeRef = useRef(false);

  useAutoClearDefaultTime({
    filters: filterOptions,
    setFilters: setFilterOptions,
    timeKey: 'createdAtRange',
    hasUserEditedTimeRef: hasUserEditedCreatedAtRangeRef,
  });

  useEffect(() => {
    setFilterOptions({ ...filters });
  }, [filters]);

  const containerClassName = useFilterContainerClassName();
  const inputContainerClassName = useFilterInputContainerClassName();

  const getDefaultCreatedAtRange = () => {
    const [startDate, endDate] = getLastWeekDateTimeRange();
    return [dayjs(startDate), dayjs(endDate)] as [Dayjs, Dayjs];
  };

  const labelAddress = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.address'));
  const labelReporterAccount = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.reporterAccount'));
  const labelReportedAt = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.reportedAt'));
  const labelStatus = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.status'));
  const col1Labels = [labelAddress, labelStatus];
  const col2Labels = [labelReporterAccount];
  const col3Labels = [labelReportedAt];

  const statusOptions = [
    { value: 'all', label: t('components.input.all') as string },
    { value: 'PENDING', label: t('mapTool.addressLibrary.locationCorrectionPage.status.pending') as string },
    { value: 'APPROVED', label: t('mapTool.addressLibrary.locationCorrectionPage.status.approved') as string },
    { value: 'REJECTED', label: t('mapTool.addressLibrary.locationCorrectionPage.status.rejected') as string },
  ];

  const [searchDrivers, { data: driverData, loading: driverLoading }] = useLazyQuery<GetDeliveryDriverAccountsForSelectResponse>(
    GET_DELIVERY_DRIVER_ACCOUNTS_FOR_SELECT,
    { fetchPolicy: 'network-only' }
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const searchValue = driverSearchText.trim();
      searchDrivers({
        variables: searchValue
          ? { name: searchValue, phone: searchValue }
          : { name: undefined, phone: undefined },
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [driverSearchText, searchDrivers]);

  const driverOptions = useMemo(() => {
    if (!driverData?.getDeliveryDriverAccountsForSelect) return [];
    return driverData.getDeliveryDriverAccountsForSelect.map((account) => ({
      label: `${account.name}${account.phone ? `(${account.phone})` : ''}`,
      value: Number(account.id),
    }));
  }, [driverData]);

  const handleReset = () => {
    const resetFilters = { ...DEFAULT_PENDING_FILTER_OPTIONS };
    hasUserEditedCreatedAtRangeRef.current = false;
    setFilterOptions(resetFilters);
    setDriverSearchText('');
    handleFilter(resetFilters);
  };

  const handleSearch = () => {
    handleFilter(filterOptions);
  };

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(col1Labels)}>{labelAddress}</span>
            <div className={inputContainerClassName}>
              <Input
                placeholder={t('mapTool.addressLibrary.locationCorrectionPage.placeholders.address') as string}
                value={filterOptions.residentialAddress}
                onChange={(e) => setFilterOptions({ ...filterOptions, residentialAddress: e.target.value })}
                allowClear
                size="middle"
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(col2Labels)}>{labelReporterAccount}</span>
            <div className={inputContainerClassName}>
              <Select
                showSearch
                allowClear
                placeholder={t('mapTool.addressLibrary.locationCorrectionPage.placeholders.reporterAccount') as string}
                value={filterOptions.creatorId}
                onChange={(value) => {
                  setFilterOptions({ ...filterOptions, creatorId: value });
                  if (value === undefined) {
                    setDriverSearchText('');
                    searchDrivers({ variables: { name: undefined, phone: undefined } });
                  }
                }}
                onSearch={setDriverSearchText}
                loading={driverLoading}
                options={driverOptions}
                filterOption={false}
                size="middle"
                className="w-full"
                dropdownStyle={{ maxWidth: '400px' }}
                notFoundContent={
                  driverLoading
                    ? t('mapTool.addressLibrary.locationCorrectionPage.messages.loading')
                    : t('mapTool.addressLibrary.locationCorrectionPage.messages.noData')
                }
              />
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(col3Labels)}>{labelReportedAt}</span>
            <div className={inputContainerClassName}>
              <RangePicker
                value={filterOptions.createdAtRange === undefined ? getDefaultCreatedAtRange() : (filterOptions.createdAtRange ?? null)}
                onChange={(dates) => {
                  hasUserEditedCreatedAtRangeRef.current = true;
                  setFilterOptions({
                    ...filterOptions,
                    createdAtRange: dates ? (dates as [Dayjs | null, Dayjs | null]) : null,
                  });
                }}
                format="YYYY/MM/DD"
                size="middle"
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(col1Labels)}>{labelStatus}</span>
            <div className={inputContainerClassName}>
              <Select
                value={filterOptions.status}
                onChange={(value) => setFilterOptions({ ...filterOptions, status: value })}
                options={statusOptions}
                size="middle"
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="min-w-0"></div>
        <div className="min-w-0"></div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button onClick={handleReset} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.reset')}</span>
        </Button>
        <Button type="primary" onClick={handleSearch} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.filter')}</span>
        </Button>
      </div>
    </div>
  );
};

export default PendingFilterArea;